import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AccountInfo } from '@azure/msal-browser';
import { AppTokenClaims } from '@app/services/authentication/auth.service';
import { AccessRights, Roles, User } from '@app/models/user';
import {AppConfig} from "@app/shared/utils/config/app-config";

@Injectable({
	providedIn: 'root'
})
export class UserService {
	private userSubject: BehaviorSubject<User> = new BehaviorSubject<User>(null);

	/**
	 * Reactive user stream — subscribe to this (not a one-time `getUser()` snapshot) in any
	 * component/guard that might run before `AppComponent`'s `MsalBroadcastService.inProgress$`
	 * subscription has called `setUserFromMsal()` (see cognito-to-msal-migration skill lesson #6:
	 * this population is now asynchronous, unlike the old Cognito `APP_INITIALIZER` flow which
	 * guaranteed synchronous availability before any component ever rendered).
	 */
	public readonly user$ = this.userSubject.asObservable();

	setUserFromMsal(account: AccountInfo) {
		const roles: Roles[] = this.getRolesFromMsalClaims(account.idTokenClaims as AppTokenClaims);
		console.log('roles', roles);
		const accessRights: AccessRights[] = Array.from(this.createAccessBy(roles));
		console.log('accessRights', accessRights);
		
		const user: User = {
			access: accessRights,
			roles: roles,
			id: account.localAccountId ?? account.homeAccountId,
			username: account.username,
			email: account.username,
			firstName: account.name?.split(' ')?.[0],
			lastName: account.name?.split(' ')?.slice(1)?.join(' '),
			accountName: (account.idTokenClaims as AppTokenClaims)?.onPremisesSamAccountName
		};

		this.userSubject.next(user);
	}

	getUser(): User {
		return this.userSubject.value;
	}

	hasAccess(access: AccessRights) {
		return this.getUser()?.access.includes(access) ?? false;
	}

	hasRole(role: Roles) {
		return this.getUser()?.roles.includes(role) ?? false;
	}

	/**
	 * v2 (2026-10-02): App Roles are now declared on each environment's Azure AD App Registration,
	 * using the EXACT same nomenclature as the `Roles` enum string values below (same naming
	 * convention as the old Cognito `custom:groups` claim). `idTokenClaims.roles` is populated once
	 * a user is assigned one of these App Roles in Azure Portal → Enterprise Applications →
	 * Users and groups. Fails CLOSED (no access) if the claim is missing/empty/unrecognized —
	 * a user not yet assigned an App Role gets zero `AccessRights`, never a silent ADMIN grant.
	 */
	private getRolesFromMsalClaims(claims: AppTokenClaims | undefined): Roles[] {
		const claimRoles = claims?.groups;
		const roles: Roles[] = [];
		for (const role in Roles) {
			const fullRoleName = AppConfig.instance.groupPrefix + Roles[role];
			if (claimRoles.includes(fullRoleName)) {
				roles.push(Roles[role]);
			}
		}

		if(roles.length === 0) {
			// eslint-disable-next-line no-console
			console.error(
				'[AuthZ] No recognized Azure AD "roles" claim found for this user — granting NO access. ' +
				'Assign an App Role to this user in Azure Portal → Enterprise Applications → Users and groups.'
			);
		}

		return roles;
	}

	private createAccessBy(roles: Roles[]): Set<AccessRights> {
		const accessRight = new Set<AccessRights>();

		for (const role of roles) {
			switch (role) {
				case Roles.VIEWER_AGENT_LIGHT:
					accessRight.add(AccessRights.REPORTING);
					accessRight.add(AccessRights.VIEW_REGISTRATION_OVERVIEW);
					accessRight.add(AccessRights.DELREG_VIEW);
					accessRight.add(AccessRights.REGVAL_VIEW);
					accessRight.add(AccessRights.MOVE_VIEW);
					accessRight.add(AccessRights.SIMULATION_VIEW);
					break;
				case Roles.PARTNER_BACKOFFICE_LIGHT:
					accessRight.add(AccessRights.REPORTING);
					accessRight.add(AccessRights.VIEW_REGISTRATION_OVERVIEW);
					accessRight.add(AccessRights.DELREG_CREATE);
					accessRight.add(AccessRights.DELREG_VIEW);
					accessRight.add(AccessRights.DELREG_EDIT);
					accessRight.add(AccessRights.DELREG_SIGN_PAPER);
					accessRight.add(AccessRights.REGVAL_VIEW);
					accessRight.add(AccessRights.RENEWAL_VIEW);
					accessRight.add(AccessRights.MOVE_VIEW);
					accessRight.add(AccessRights.SIMULATION_VIEW);
					break;
			case Roles.ENCODING:
				accessRight.add(AccessRights.REPORTING);
				accessRight.add(AccessRights.VIEW_REGISTRATION_OVERVIEW);
				accessRight.add(AccessRights.DELREG_CREATE);
				accessRight.add(AccessRights.DELREG_VIEW);
				accessRight.add(AccessRights.DELREG_EDIT);
				accessRight.add(AccessRights.DELREG_SIGN_PAPER);
				accessRight.add(AccessRights.REGVAL_VIEW);
				accessRight.add(AccessRights.RENEWAL_VIEW);
				accessRight.add(AccessRights.REGVAL_EDIT);
				accessRight.add(AccessRights.MOVE_CREATE);
				accessRight.add(AccessRights.MOVE_VIEW);
				accessRight.add(AccessRights.MOVE_SAVE);
				accessRight.add(AccessRights.SIMULATION_VIEW);
				accessRight.add(AccessRights.SIMULATION_CREATE);
				accessRight.add(AccessRights.SIMULATION_REPORTING);
				accessRight.add(AccessRights.PRODUCT_SERVICES_VIEW);
				accessRight.add(AccessRights.PRODUCT_SERVICES_CREATE);
				break;
				case Roles.VALIDATION:
					accessRight.add(AccessRights.REPORTING);
					accessRight.add(AccessRights.VIEW_REGISTRATION_OVERVIEW);
					accessRight.add(AccessRights.DELREG_CREATE);
					accessRight.add(AccessRights.DELREG_VIEW);
					accessRight.add(AccessRights.DELREG_EDIT);
					accessRight.add(AccessRights.DELREG_SIGN_PAPER);
					accessRight.add(AccessRights.REGVAL_VIEW);
					accessRight.add(AccessRights.RENEWAL_VIEW);
					accessRight.add(AccessRights.MOVE_CREATE);
					accessRight.add(AccessRights.MOVE_VIEW);
					accessRight.add(AccessRights.MOVE_SAVE);
					accessRight.add(AccessRights.MOVE_UPDATE);
					accessRight.add(AccessRights.MOVE_WORKING_LIST);
					accessRight.add(AccessRights.SIMULATION_VIEW);
					accessRight.add(AccessRights.SIMULATION_CREATE);
					accessRight.add(AccessRights.SIMULATION_REPORTING);
					accessRight.add(AccessRights.SIMULATION_PROMO_ASSIGN);
					break;
				case Roles.SALES_AGENT_LIGHT:
					accessRight.add(AccessRights.REPORTING);
					accessRight.add(AccessRights.VIEW_REGISTRATION_OVERVIEW);
					accessRight.add(AccessRights.DELREG_VIEW);
					accessRight.add(AccessRights.DELREG_CREATE);
					accessRight.add(AccessRights.DELREG_EDIT);
					accessRight.add(AccessRights.DELREG_SIGN_PAPER);
					accessRight.add(AccessRights.DELREG_SEND_SIGNATURE);
					accessRight.add(AccessRights.DELREG_SEND_OFFER);
					accessRight.add(AccessRights.DELREG_RESEND_OFFER);
					accessRight.add(AccessRights.DELREG_ARCHIVE);
					accessRight.add(AccessRights.DELREG_POSTPONE);
					accessRight.add(AccessRights.REGVAL_VIEW);
					accessRight.add(AccessRights.MOVE_VIEW);
					accessRight.add(AccessRights.SIMULATION_VIEW);
					break;
			case Roles.NURSING_AGENT_LIGHT:
				accessRight.add(AccessRights.REPORTING);
				accessRight.add(AccessRights.VIEW_REGISTRATION_OVERVIEW);
				accessRight.add(AccessRights.VIEW_WORKLIST_OVERVIEW);
				accessRight.add(AccessRights.DELREG_VIEW);
				accessRight.add(AccessRights.DELREG_CREATE);
				accessRight.add(AccessRights.DELREG_EDIT);
				accessRight.add(AccessRights.DELREG_SIGN_PAPER);
				accessRight.add(AccessRights.DELREG_SEND_SIGNATURE);
				accessRight.add(AccessRights.DELREG_SEND_OFFER);
				accessRight.add(AccessRights.DELREG_RESEND_OFFER);
				accessRight.add(AccessRights.DELREG_ARCHIVE);
				accessRight.add(AccessRights.DELREG_POSTPONE);
				accessRight.add(AccessRights.REGVAL_VALIDATION_CONTROL);
				accessRight.add(AccessRights.REGVAL_VIEW);
				accessRight.add(AccessRights.REGVAL_EDIT);
				accessRight.add(AccessRights.REGVAL_RETRY);
				accessRight.add(AccessRights.REGVAL_ARCHIVE);
				accessRight.add(AccessRights.MOVE_VIEW);
				accessRight.add(AccessRights.SIMULATION_VIEW);
				accessRight.add(AccessRights.PRODUCT_SERVICES_VIEW);
				accessRight.add(AccessRights.PRODUCT_SERVICES_CREATE);
				break;
				case Roles.INTERNAL_BACKOFFICE_LIGHT:
					accessRight.add(AccessRights.REPORTING);
					accessRight.add(AccessRights.VIEW_REGISTRATION_OVERVIEW);
					accessRight.add(AccessRights.DELREG_VIEW);
					accessRight.add(AccessRights.DELREG_CREATE);
					accessRight.add(AccessRights.VIEW_WORKLIST_OVERVIEW);
					accessRight.add(AccessRights.DELREG_EDIT);
					accessRight.add(AccessRights.DELREG_SIGN_PAPER);
					accessRight.add(AccessRights.DELREG_SEND_SIGNATURE);
					accessRight.add(AccessRights.REGVAL_VALIDATION_CONTROL);
					accessRight.add(AccessRights.DELREG_SEND_OFFER);
					accessRight.add(AccessRights.DELREG_RESEND_OFFER);
					accessRight.add(AccessRights.DELREG_ARCHIVE);
					accessRight.add(AccessRights.DELREG_POSTPONE);
					accessRight.add(AccessRights.REGVAL_VIEW);
					accessRight.add(AccessRights.RENEWAL_VIEW);
					accessRight.add(AccessRights.MOVE_VIEW);
					accessRight.add(AccessRights.SIMULATION_VIEW);
					break;
			case Roles.NURSING_AGENT_EXPERT:
				accessRight.add(AccessRights.REPORTING);
				accessRight.add(AccessRights.VIEW_REGISTRATION_OVERVIEW);
				accessRight.add(AccessRights.VIEW_WORKLIST_OVERVIEW);
				accessRight.add(AccessRights.DELREG_VIEW);
				accessRight.add(AccessRights.DELREG_CREATE);
				accessRight.add(AccessRights.DELREG_EDIT);
				accessRight.add(AccessRights.DELREG_SIGN_PAPER);
				accessRight.add(AccessRights.DELREG_SEND_SIGNATURE);
				accessRight.add(AccessRights.DELREG_SEND_OFFER);
				accessRight.add(AccessRights.DELREG_RESEND_OFFER);
				accessRight.add(AccessRights.DELREG_ARCHIVE);
				accessRight.add(AccessRights.DELREG_POSTPONE);
				accessRight.add(AccessRights.REGVAL_VALIDATION_CONTROL);
				accessRight.add(AccessRights.REGVAL_VIEW);
				accessRight.add(AccessRights.REGVAL_EDIT);
				accessRight.add(AccessRights.REGVAL_RETRY);
				accessRight.add(AccessRights.REGVAL_ARCHIVE);
				accessRight.add(AccessRights.REGVAL_RETRY_ACTIVATION);
				accessRight.add(AccessRights.MOVE_VIEW);
				accessRight.add(AccessRights.SIMULATION_VIEW);
				accessRight.add(AccessRights.PRODUCT_SERVICES_VIEW);
				accessRight.add(AccessRights.PRODUCT_SERVICES_CREATE);
				break;
				case Roles.INTERNAL_BACKOFFICE_EXPERT:
					accessRight.add(AccessRights.REPORTING);
					accessRight.add(AccessRights.VIEW_REGISTRATION_OVERVIEW);
					accessRight.add(AccessRights.VIEW_WORKLIST_OVERVIEW);
					accessRight.add(AccessRights.DELREG_VIEW);
					accessRight.add(AccessRights.DELREG_CREATE);
					accessRight.add(AccessRights.DELREG_EDIT);
					accessRight.add(AccessRights.DELREG_SIGN_PAPER);
					accessRight.add(AccessRights.DELREG_SEND_SIGNATURE);
					accessRight.add(AccessRights.DELREG_SEND_OFFER);
					accessRight.add(AccessRights.DELREG_RESEND_OFFER);
					accessRight.add(AccessRights.DELREG_ARCHIVE);
					accessRight.add(AccessRights.DELREG_POSTPONE);
					accessRight.add(AccessRights.REGVAL_VALIDATION_CONTROL);
					accessRight.add(AccessRights.REGVAL_VIEW);
					accessRight.add(AccessRights.REGVAL_EDIT);
					accessRight.add(AccessRights.REGVAL_RETRY);
					accessRight.add(AccessRights.REGVAL_ARCHIVE);
					accessRight.add(AccessRights.REGVAL_UNARCHIVE);
					accessRight.add(AccessRights.REGVAL_RETRY_ACTIVATION);
					accessRight.add(AccessRights.RENEWAL_VIEW);
					accessRight.add(AccessRights.MOVE_VIEW);
					accessRight.add(AccessRights.SIMULATION_VIEW);
					accessRight.add(AccessRights.PRODUCT_SERVICES_CREATE);
					accessRight.add(AccessRights.PRODUCT_SERVICES_VIEW);
					break;
			case Roles.INTERNAL_CREDIT_COLLECTION_EXPERT:
				accessRight.add(AccessRights.REPORTING);
				accessRight.add(AccessRights.VIEW_REGISTRATION_OVERVIEW);
				accessRight.add(AccessRights.VIEW_WORKLIST_OVERVIEW);
				accessRight.add(AccessRights.DELREG_VIEW);
				accessRight.add(AccessRights.DELREG_CREATE);
				accessRight.add(AccessRights.DELREG_EDIT);
				accessRight.add(AccessRights.DELREG_SIGN_PAPER);
				accessRight.add(AccessRights.DELREG_SEND_SIGNATURE);
				accessRight.add(AccessRights.DELREG_SEND_OFFER);
				accessRight.add(AccessRights.DELREG_RESEND_OFFER);
				accessRight.add(AccessRights.DELREG_ARCHIVE);
				accessRight.add(AccessRights.DELREG_POSTPONE);
				accessRight.add(AccessRights.REGVAL_VIEW);
				accessRight.add(AccessRights.REGVAL_EDIT);
				accessRight.add(AccessRights.REGVAL_RETRY);
				accessRight.add(AccessRights.REGVAL_VALIDATION_CONTROL);
				accessRight.add(AccessRights.REGVAL_SCREENING_UNJUSTIFIED);
				accessRight.add(AccessRights.REGVAL_CHECKFRAUD_UNJUSTIFIED);
				accessRight.add(AccessRights.REGVAL_ARCHIVE);
				accessRight.add(AccessRights.MOVE_VIEW);
				accessRight.add(AccessRights.SIMULATION_VIEW);
				accessRight.add(AccessRights.PRODUCT_SERVICES_VIEW);
				accessRight.add(AccessRights.PRODUCT_SERVICES_CREATE);
				break;
				case Roles.BUSINESS_ADMIN:
				case Roles.ADMIN:
					accessRight.add(AccessRights.REPORTING);
					accessRight.add(AccessRights.VIEW_REGISTRATION_OVERVIEW);
					accessRight.add(AccessRights.VIEW_WORKLIST_OVERVIEW);
					accessRight.add(AccessRights.VIEW_BUYING_GROUP);
					accessRight.add(AccessRights.VIEW_DOCUMENTS_TEMPLATE_MANAGER);
					accessRight.add(AccessRights.VIEW_PIPELINE_DATA);
					accessRight.add(AccessRights.DELREG_VIEW);
					accessRight.add(AccessRights.DELREG_CREATE);
					accessRight.add(AccessRights.DELREG_EDIT);
					accessRight.add(AccessRights.DELREG_SIGN_PAPER);
					accessRight.add(AccessRights.DELREG_SEND_SIGNATURE);
					accessRight.add(AccessRights.DELREG_SEND_OFFER);
					accessRight.add(AccessRights.DELREG_RESEND_OFFER);
					accessRight.add(AccessRights.DELREG_ARCHIVE);
					accessRight.add(AccessRights.DELREG_POSTPONE);
					accessRight.add(AccessRights.REGVAL_VIEW);
					accessRight.add(AccessRights.REGVAL_EDIT);
					accessRight.add(AccessRights.REGVAL_RETRY);
					accessRight.add(AccessRights.REGVAL_FORCE);
					accessRight.add(AccessRights.REGVAL_VALIDATION_CONTROL);
					accessRight.add(AccessRights.REGVAL_SCREENING_UNJUSTIFIED);
					accessRight.add(AccessRights.REGVAL_CHECKFRAUD_UNJUSTIFIED);
					accessRight.add(AccessRights.REGVAL_ARCHIVE);
					accessRight.add(AccessRights.REGVAL_RETRY_ACTIVATION);
					accessRight.add(AccessRights.REGVAL_UNARCHIVE);
					accessRight.add(AccessRights.RENEWAL_VIEW);
					accessRight.add(AccessRights.MOVE_VIEW);
					accessRight.add(AccessRights.MOVE_CREATE);
					accessRight.add(AccessRights.MOVE_SAVE);
					accessRight.add(AccessRights.MOVE_UPDATE);
					accessRight.add(AccessRights.MOVE_ASSIGN);
					accessRight.add(AccessRights.MOVE_WORKING_LIST);
					accessRight.add(AccessRights.MOVE_UNARCHIVE);
					accessRight.add(AccessRights.SIMULATION_VIEW);
					accessRight.add(AccessRights.SIMULATION_CREATE);
					accessRight.add(AccessRights.SIMULATION_REPORTING);
					accessRight.add(AccessRights.SIMULATION_PROMO_ASSIGN);
					accessRight.add(AccessRights.REGVAL_CHANGE_STATUS_ACTIVATION_FAILED);
					accessRight.add(AccessRights.PRODUCT_SERVICES_CREATE);
					accessRight.add(AccessRights.PRODUCT_SERVICES_VIEW);
					break;
			}
		}

		return accessRight;
	}

	public hasAccessPromoCodeEdition(): boolean {
		return this.getUser().roles.some(
			(role) =>
				role === Roles.BUSINESS_ADMIN ||
				role === Roles.INTERNAL_BACKOFFICE_LIGHT ||
				role === Roles.PARTNER_BACKOFFICE_LIGHT ||
				role === Roles.INTERNAL_BACKOFFICE_EXPERT
		);
	}

	hasManagerRole(): boolean {
		return this.getUser().roles.some(
			(role) =>
				role === Roles.BUSINESS_ADMIN ||
				role === Roles.ADMIN ||
				role === Roles.NURSING_AGENT_EXPERT ||
				role === Roles.INTERNAL_BACKOFFICE_EXPERT ||
				role === Roles.INTERNAL_CREDIT_COLLECTION_EXPERT
		);
	}
}
